<script setup>
import { onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import TrustedForwardersAPI from 'dashboard/api/borderchatTrustedForwarders';
import Button from 'dashboard/components-next/button/Button.vue';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';

const EMAIL = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;
const isLoading = ref(true);
const isSaving = ref(false);
const addresses = ref([]);
const inboxes = ref([]);
const providers = ref([]);
const limit = ref(20);
const newAddress = ref('');

const apply = data => {
  addresses.value = Array.isArray(data?.forwarders) ? [...data.forwarders] : [];
  inboxes.value = Array.isArray(data?.inboxes) ? data.inboxes : [];
  providers.value = Array.isArray(data?.supported_providers)
    ? data.supported_providers
    : [];
  limit.value = data?.limit || 20;
};

const loadForwarders = async () => {
  try {
    isLoading.value = true;
    const { data } = await TrustedForwardersAPI.get();
    apply(data);
  } catch {
    useAlert('无法读取可信转发人。');
  } finally {
    isLoading.value = false;
  }
};

const addAddress = () => {
  const address = newAddress.value.trim().toLowerCase();
  if (!address) return;
  if (!EMAIL.test(address)) {
    useAlert('请输入正确的邮箱地址。');
    return;
  }
  if (!addresses.value.includes(address)) {
    if (addresses.value.length >= limit.value) {
      useAlert(`最多可添加 ${limit.value} 个转发人。`);
      return;
    }
    addresses.value.push(address);
  }
  newAddress.value = '';
};

const removeAddress = address => {
  addresses.value = addresses.value.filter(item => item !== address);
};

const saveAddresses = async () => {
  try {
    isSaving.value = true;
    const { data } = await TrustedForwardersAPI.update(addresses.value);
    apply(data);
    useAlert('可信转发人已保存。');
  } catch {
    useAlert('保存可信转发人失败。');
  } finally {
    isSaving.value = false;
  }
};

onMounted(loadForwarders);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
<template>
  <SettingsLayout :is-loading="isLoading" loading-message="正在读取可信转发人…">
    <template #header>
      <BaseSettingsHeader
        title="可信转发人"
        description="整个账户共用一份名单，对所有普通邮件收件箱生效。"
      />
    </template>

    <template #body>
      <SectionLayout
        title="转发人名单"
        description="名单里的邮箱把客户邮件转发进任何邮件收件箱时，邮件会按原客户的邮箱归入该客户自己的会话，回复直接发给客户。只认通过发件域名验证的邮件；读不出原客户时仍归入转发人的会话，并标记“转发未识别”。"
      >
        <div class="grid max-w-2xl gap-4" data-trusted-forwarders>
          <div class="flex min-h-8 flex-wrap gap-2">
            <span
              v-for="address in addresses"
              :key="address"
              class="inline-flex items-center gap-1 rounded-full bg-n-alpha-2 px-3 py-1 text-sm text-n-slate-12"
            >
              {{ address }}
              <button
                type="button"
                class="text-n-slate-10 hover:text-n-slate-12"
                :aria-label="`删除转发人 ${address}`"
                @click="removeAddress(address)"
              >
                ×
              </button>
            </span>
            <span v-if="!addresses.length" class="text-sm text-n-slate-10">
              尚未添加转发人
            </span>
          </div>

          <div class="flex flex-col gap-2 sm:flex-row">
            <input
              v-model="newAddress"
              type="email"
              maxlength="254"
              placeholder="例如：yourname@gmail.com"
              class="h-10 flex-1 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-n-slate-12"
              @keydown.enter.prevent="addAddress"
            />
            <Button type="button" slate outline label="添加" @click="addAddress" />
            <Button
              type="button"
              label="保存转发人"
              :is-loading="isSaving"
              @click="saveAddresses"
            />
          </div>
        </div>
      </SectionLayout>

      <SectionLayout
        with-border
        title="生效范围"
        description="亚马逊邮箱按订单处理，不参与转发识别。以后新增的邮件收件箱也会自动使用这份名单。"
      >
        <ul
          v-if="inboxes.length"
          class="flex list-none flex-wrap gap-2 text-sm"
          data-trusted-forwarder-inboxes
        >
          <li
            v-for="inbox in inboxes"
            :key="inbox.id"
            class="rounded-full px-3 py-1"
            :class="
              inbox.provider
                ? 'bg-n-alpha-2 text-n-slate-12'
                : 'bg-n-amber-3 text-n-amber-11'
            "
          >
            {{ inbox.name }}
            <span class="text-n-slate-10">
              · {{ inbox.provider || '收信服务商未识别，转发不会被识别' }}
            </span>
          </li>
        </ul>
        <p v-else class="text-sm text-n-slate-10">
          当前没有普通邮件收件箱。
        </p>
        <p
          v-if="providers.length"
          class="mt-3 text-sm text-n-slate-11"
          data-trusted-forwarder-providers
        >
          支持的收信服务商：{{ providers.join('、') }}。只认收件箱自己服务商写入的验证结果。
        </p>
      </SectionLayout>
    </template>
  </SettingsLayout>
</template>
